import React, { useMemo } from 'react';

import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';
import type { StatusConfig, StatusTone } from '../StatusTag';

export type { StatusConfig, StatusTone };

export interface StatusDotProps extends Styleable {
    /** The raw status value from the caller's domain (e.g. `'published'`, `'revoked'`). */
    status: string;
    /** Caller-owned map of every status this screen knows about to its display config. */
    config: Record<string, StatusConfig>;
    /** Used when `status` is absent from `config`, instead of the raw-string default. */
    fallback?: StatusConfig;
    /**
     * EAA-1804: fades the dot and strikes through the label — for a status
     * that has since been superseded/stopped, distinct from a plain "default"
     * tone which is just neutral, not historical.
     */
    muted?: boolean;
}

/**
 * Tier-1 presentational primitive: the sheet's small inline status cue — a
 * colored dot plus a plain-text label, as opposed to `StatusTag`'s filled
 * pill. Deliberately mirrors `StatusTag`'s `status`/`config`/`fallback`
 * contract exactly (same `StatusTone` vocabulary, re-exported rather than
 * redeclared) so a screen can pass the very same config map to either
 * component depending on how loud the status needs to read in that spot.
 */
export function StatusDot({
    status,
    config,
    fallback,
    muted = false,
    className,
    style,
}: StatusDotProps): React.ReactElement {
    const colors = useUIColors();

    // EAA-1804: same tone->accent shape StatusTag builds, but only the accent
    // (its old "border" slot) is needed here — the dot has no fill/border pair.
    const toneMap = useMemo<Record<StatusTone, string>>(
        () => ({
            default: colors.border,
            success: colors.success,
            warning: colors.warning,
            error: colors.error,
            processing: colors.primary,
        }),
        [colors]
    );

    const resolved: StatusConfig = config[status] ?? fallback ?? { label: status, tone: 'default' };
    const accent = toneMap[resolved.tone];

    return (
        <span
            className={cx('ui-status-dot', className)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,

                ...style,
            }}
        >
            <span
                style={{
                    flex: 'none',
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: accent,
                    opacity: muted ? 0.5 : 1,
                }}
            />
            <span
                style={{
                    color: muted ? colors.textMuted : colors.text,
                    textDecoration: muted ? 'line-through' : undefined,
                }}
            >
                {resolved.label}
            </span>
        </span>
    );
}

export default StatusDot;
